import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Modal, message, Input, Popconfirm, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ShareAltOutlined, CopyOutlined, FacebookOutlined, TwitterOutlined, LinkedinOutlined, WhatsAppOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { getAllBlogPosts, deleteBlogPost, publishBlogPost, unpublishBlogPost } from '../../../api/blogApi';
import moment from 'moment';
import { MS_URL } from '../../../settings';
import '../../../assets/scss/components/BlogPost/blog.scss';

const { Search } = Input;

const BlogList = () => {
  const history = useHistory();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedBlogForShare, setSelectedBlogForShare] = useState(null);
  const [shareTextModalVisible, setShareTextModalVisible] = useState(false);
  const [currentShareText, setCurrentShareText] = useState('');
  const [currentSharePlatform, setCurrentSharePlatform] = useState('');

  useEffect(() => {
    fetchBlogPosts(pagination.current - 1, pagination.pageSize);
  }, []);

  const fetchBlogPosts = async (page = 0, size = 10) => {
    setLoading(true);
    try {
      const data = await getAllBlogPosts(page, size);
      console.log('=== Blog Posts Paginated Response ===');
      console.log('Page:', data.page, 'Size:', data.size);
      console.log('Total Elements:', data.totalElements, 'Total Pages:', data.totalPages);
      console.log('Content:', data.content);
      console.log('====================================');
      setBlogPosts(data.content);
      setPagination({
        current: data.page + 1,
        pageSize: data.size,
        total: data.totalElements,
      });
    } catch (error) {
      message.error('Failed to fetch blog posts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    // Note: Search is now local filtering. For server-side search, create a separate endpoint
  };

  const handleTableChange = (paginationConfig) => {
    const page = paginationConfig.current - 1;
    const size = paginationConfig.pageSize;
    fetchBlogPosts(page, size);
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlogPost(id);
      message.success('Blog post deleted successfully');
      fetchBlogPosts(pagination.current - 1, pagination.pageSize);
    } catch (error) {
      message.error('Failed to delete blog post');
      console.error(error);
    }
  };

  const handlePublishToggle = async (record) => {
    try {
      if (record.published) {
        await unpublishBlogPost(record.id);
        message.success('Blog post unpublished successfully');
      } else {
        await publishBlogPost(record.id);
        message.success('Blog post published successfully');
      }
      fetchBlogPosts(pagination.current - 1, pagination.pageSize);
    } catch (error) {
      message.error('Failed to update publish status');
      console.error(error);
    }
  };

  const handleShare = (record) => {
    setSelectedBlogForShare(record);
    setShareModalVisible(true);
  };

  const getShareText = (record) => {
    let shareText = record.title;
    
    // Add meta description or excerpt if available
    const description = record.metaDescription || record.excerpt;
    if (description) {
      shareText += `\n\n${description}`;
    }
    
    // Add hashtags from meta keywords and regular tags
    const hashtags = [];
    
    if (record.metaKeywords) {
      const keywords = record.metaKeywords.split(',').map(k => k.trim());
      keywords.forEach(keyword => {
        const tag = keyword.replace(/\s+/g, '');
        if (tag) hashtags.push(`#${tag}`);
      });
    }
    
    if (record.tags && record.tags.length > 0) {
      record.tags.forEach(tag => {
        const cleanTag = tag.replace(/\s+/g, '');
        if (cleanTag && !hashtags.includes(`#${cleanTag}`)) {
          hashtags.push(`#${cleanTag}`);
        }
      });
    }
    
    // Add hashtags to share text
    if (hashtags.length > 0) {
      shareText += `\n\n${hashtags.join(' ')}`;
    }
    
    // Add URL
    shareText += `\n\n${getPublicBlogUrl(record.slug)}`;
    
    return shareText;
  };

  const getPublicBlogUrl = (slug) => {
    // Fixed to Sri Lanka only
    return `${MS_URL}/Sri-Lanka/blog/${slug}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('Share text copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy text');
    });
  };

  const shareOnFacebook = (record) => {
    const shareText = getShareText(record);
    const url = getPublicBlogUrl(record.slug);
    
    // Copy share text to clipboard first
    navigator.clipboard.writeText(shareText).then(() => {
      message.success('Share text copied! Paste it in Facebook post.');
      // Open Facebook sharer in fullscreen
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    }).catch(() => {
      // Fallback: show modal with share text
      setCurrentShareText(shareText);
      setCurrentSharePlatform('Facebook');
      setShareTextModalVisible(true);
    });
  };

  const shareOnTwitter = (record) => {
    let text = record.title;
    const description = record.metaDescription || record.excerpt;
    if (description) {
      text += ` - ${description.substring(0, 100)}${description.length > 100 ? '...' : ''}`;
    }
    
    // Add hashtags
    const hashtags = [];
    if (record.metaKeywords) {
      const keywords = record.metaKeywords.split(',').map(k => k.trim().replace(/\s+/g, ''));
      hashtags.push(...keywords.slice(0, 3));
    }
    if (record.tags && hashtags.length < 3) {
      const cleanTags = record.tags.map(t => t.replace(/\s+/g, ''));
      hashtags.push(...cleanTags.slice(0, 3 - hashtags.length));
    }
    
    const url = getPublicBlogUrl(record.slug);
    const encodedText = encodeURIComponent(text);
    const hashtagParam = hashtags.length > 0 ? `&hashtags=${hashtags.join(',')}` : '';
    
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodedText}${hashtagParam}`, '_blank');
  };

  const shareOnLinkedIn = (record) => {
    const shareText = getShareText(record);
    const url = getPublicBlogUrl(record.slug);
    
    // Copy share text to clipboard first
    navigator.clipboard.writeText(shareText).then(() => {
      message.success('Share text copied! Paste it in LinkedIn post.');
      // Open LinkedIn sharer in fullscreen
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    }).catch(() => {
      // Fallback: show modal with share text
      setCurrentShareText(shareText);
      setCurrentSharePlatform('LinkedIn');
      setShareTextModalVisible(true);
    });
  };

  const shareOnWhatsApp = (record) => {
    const shareText = getShareText(record);
    const text = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Filter posts locally for search
  const filteredPosts = searchText
    ? blogPosts.filter(post =>
        post.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        post.author?.toLowerCase().includes(searchText.toLowerCase()) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
      )
    : blogPosts;

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      render: (text, record) => (
        <div className="blog-title-cell">
          <div className="title-text">{text}</div>
          <div className="slug-text">/{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      width: '12%',
    },
    {
      title: 'Status',
      dataIndex: 'published',
      key: 'published',
      width: '10%',
      render: (published) => (
        <Tag color={published ? 'green' : 'orange'}>
          {published ? 'Published' : 'Draft'}
        </Tag>
      ),
      filters: [
        { text: 'Published', value: true },
        { text: 'Draft', value: false },
      ],
      onFilter: (value, record) => record.published === value,
    },
    // {
    //   title: 'Categories',
    //   dataIndex: 'categories',
    //   key: 'categories',
    //   width: '15%',
    //   render: (categories) => (
    //     <>
    //       {categories?.slice(0, 2).map(cat => (
    //         <Tag key={cat} color="blue">{cat}</Tag>
    //       ))}
    //       {categories?.length > 2 && <Tag>+{categories.length - 2}</Tag>}
    //     </>
    //   ),
    // },
    {
      title: 'Published Date',
      dataIndex: 'publishedDate',
      key: 'publishedDate',
      width: '12%',
      render: (date) => date ? moment(date).format('MMM DD, YYYY') : '-',
      sorter: (a, b) => {
        if (!a.publishedDate) return 1;
        if (!b.publishedDate) return -1;
        return new Date(a.publishedDate) - new Date(b.publishedDate);
      },
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: '8%',
      render: (order) => order || 0,
      sorter: (a, b) => (a.order || 0) - (b.order || 0),
    },
    {
      title: 'Views',
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: '8%',
      render: (count) => count || 0,
      sorter: (a, b) => (a.viewCount || 0) - (b.viewCount || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => (
        <Space size="small" wrap>
          {/* <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => history.push(`/dashboard/blog-posts/view/${record.id}`)}
          >
            View
          </Button> */}
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => history.push(`/dashboard/blog-posts/edit/${record.id}`)}
          >
            Edit
          </Button>
          {record.published && (
            <Button
              type="link"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={() => handleShare(record)}
            >
              Share
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={record.published ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            onClick={() => handlePublishToggle(record)}
          >
            {record.published ? 'Unpublish' : 'Publish'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this blog post?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            placement="left"
            overlayStyle={{ maxWidth: '300px' }}
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="blog-list-container">
      <div className="blog-list-header">
        <h2>Blog Posts</h2>
        <div className="blog-list-actions">
          <Search
            placeholder="Search blog posts..."
            allowClear
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push('/dashboard/blog-posts/create')}
          >
            Create New Post
          </Button>
        </div>
      </div>
      <div className="blog-list-card">
        <Table
          columns={columns}
          dataSource={filteredPosts}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} posts`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* Share Modal */}
      <Modal
        title={`Share: ${selectedBlogForShare?.title}`}
        open={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        footer={null}
        width={600}
        className="blog-share-modal"
      >
        {selectedBlogForShare && (
          <div className="blog-share-content">
            {/* Public URL Display */}
            <div className="share-url-section">
              <div className="section-label">Public URL (Sri Lanka):</div>
              <Input.Group compact className="url-input-group">
                <Input
                  value={getPublicBlogUrl(selectedBlogForShare.slug)}
                  readOnly
                  className="url-input"
                />
                <Tooltip title="Copy link">
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(getPublicBlogUrl(selectedBlogForShare.slug))}
                  >
                    Copy Link
                  </Button>
                </Tooltip>
              </Input.Group>
              
              {/* Copy Full Share Text Button */}
              <div className="copy-share-text">
                <Button
                  type="dashed"
                  block
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(getShareText(selectedBlogForShare))}
                >
                  Copy Full Share Text (with description & hashtags)
                </Button>
              </div>
              
              {/* Meta Description */}
              {selectedBlogForShare.metaDescription && (
                <div className="meta-section">
                  <div className="meta-label">Meta Description:</div>
                  <div className="meta-content">
                    {selectedBlogForShare.metaDescription}
                  </div>
                </div>
              )}
              
              {/* Meta Keywords/Tags */}
              {selectedBlogForShare.metaKeywords && (
                <div className="meta-section">
                  <div className="meta-label">Meta Tags:</div>
                  <div className="meta-tags">
                    {selectedBlogForShare.metaKeywords.split(',').map((keyword, index) => (
                      <Tag key={index} color="blue" className="meta-tag">
                        #{keyword.trim()}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Regular Tags */}
              {selectedBlogForShare.tags && selectedBlogForShare.tags.length > 0 && (
                <div className="meta-section">
                  <div className="meta-label">Content Tags:</div>
                  <div className="meta-tags">
                    {selectedBlogForShare.tags.map((tag, index) => (
                      <Tag key={index} color="green" className="meta-tag">
                        #{tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Social Media Share Buttons */}
            <div className="social-share-section">
              <div className="section-label">Share on Social Media:</div>
              <Space size="middle" wrap>
                <Button
                  type="primary"
                  icon={<FacebookOutlined />}
                  onClick={() => shareOnFacebook(selectedBlogForShare)}
                  className="social-btn facebook-btn"
                >
                  Facebook
                </Button>
                <Button
                  type="primary"
                  icon={<TwitterOutlined />}
                  onClick={() => shareOnTwitter(selectedBlogForShare)}
                  className="social-btn twitter-btn"
                >
                  Twitter
                </Button>
                <Button
                  type="primary"
                  icon={<LinkedinOutlined />}
                  onClick={() => shareOnLinkedIn(selectedBlogForShare)}
                  className="social-btn linkedin-btn"
                >
                  LinkedIn
                </Button>
                <Button
                  type="primary"
                  icon={<WhatsAppOutlined />}
                  onClick={() => shareOnWhatsApp(selectedBlogForShare)}
                  className="social-btn whatsapp-btn"
                >
                  WhatsApp
                </Button>
              </Space>
              
              <div className="share-note">
                <InfoCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                <span>Facebook & LinkedIn: Share text will be auto-copied, then paste it when the share window opens.</span>
              </div>
            </div>

            {/* Preview Info */}
            <div className="share-preview-info">
              <div className="info-note">
                <strong>Note:</strong> When sharing, social media platforms will display:
              </div>
              <ul className="info-list">
                <li>Blog Title: {selectedBlogForShare.title}</li>
                <li>Blog Image: {selectedBlogForShare.featuredImage ? 'Featured Image' : 'Default Image'}</li>
                <li>Description: {selectedBlogForShare.excerpt ? 'Blog Excerpt' : 'Auto-generated'}</li>
              </ul>
              <div className="info-link">
                Use <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer">Facebook Debugger</a> to preview and refresh cached data.
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BlogList;
